import { copy, type Goal } from '@qi/shared';
import { GoalCard, SectionReveal, TrustBadge } from '@qi/ui';
import { useNavigate } from '@tanstack/react-router';
import { Battery, Crosshair, Lock, Moon, Mountain, Wind, Zap } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useUpdateProfile } from '../lib/queries.ts';
import { useSessionStore } from '../stores/session-store.ts';

const GOALS: { id: Goal; icon: ReactNode }[] = [
  { id: 'stress', icon: <Wind size={20} strokeWidth={1.6} /> },
  { id: 'sleep', icon: <Moon size={20} strokeWidth={1.6} /> },
  { id: 'focus', icon: <Crosshair size={20} strokeWidth={1.6} /> },
  { id: 'recovery', icon: <Battery size={20} strokeWidth={1.6} /> },
  { id: 'energy', icon: <Zap size={20} strokeWidth={1.6} /> },
  { id: 'meditation', icon: <Mountain size={20} strokeWidth={1.6} /> },
];

/** The goals offered in the picker, shared with the marketing route's `?goal=` validation. */
export const VALID_GOALS: readonly Goal[] = GOALS.map((g) => g.id);

interface GoalPickerScreenProps {
  /** Pre-selected goal (e.g. from a marketing `?goal=` deep link). */
  preselectedGoal?: Goal;
  /** Mono eyebrow above the title. */
  eyebrow?: string;
  /** Navigation override fired after the goal is mirrored into the store. The
   *  cold funnel passes this to advance into `/start/*` instead of the default
   *  in-app `/scan/bioscan`. Receives the chosen goal. */
  onPicked?: (goal: Goal) => void;
}

/**
 * Shared goal-picker used by both the marketing onboarding route (guests,
 * pre-sign-up) and the in-app `/goal` route (signed-in users). On Continue it
 * always mirrors the goal into the session store and heads into the scan flow;
 * for a real signed-in user it also persists to the profile (PATCH /me) so the
 * choice survives across devices. Guests/anonymous sessions can't PATCH (the
 * API rejects anonymous users) — their goal is persisted on sign-up instead.
 */
export function GoalPickerScreen({
  preselectedGoal,
  eyebrow = 'Onboarding',
  onPicked,
}: GoalPickerScreenProps) {
  const [goal, setGoal] = useState<Goal | null>(preselectedGoal ?? null);
  const user = useSessionStore((s) => s.user);
  const setGoalInStore = useSessionStore((s) => s.setGoal);
  const updateProfile = useUpdateProfile();
  const navigate = useNavigate();

  // Picking a goal advances straight into the scan — no separate Continue step.
  function onSelect(next: Goal) {
    setGoal(next);
    setGoalInStore(next);
    if (user && user.email !== '(guest)') {
      // Fire-and-forget; a failure leaves the goal in the store and it can be
      // re-saved from Settings — it must not block starting the scan.
      updateProfile.mutate({ goal: next });
    }
    if (onPicked) {
      onPicked(next);
      return;
    }
    navigate({ to: '/scan/bioscan' });
  }

  return (
    <div className="mx-auto max-w-6xl space-y-10 px-6 py-12">
      <header className="flex flex-col items-center gap-3 text-center">
        <TrustBadge icon={<Lock size={11} strokeWidth={2.2} />}>
          Local-first · we never upload raw biometrics
        </TrustBadge>
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">{eyebrow}</p>
        <h1 className="font-display text-5xl text-ink-0">{copy.onboarding.goalPickerTitle}</h1>
        <p className="mx-auto max-w-xl text-ink-1">{copy.onboarding.goalPickerBody}</p>
      </header>

      {/* 6 goals — symmetric 2×3 grid at lg so we never get an orphaned row.
          Stacks 1→2→3 columns up the breakpoints. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GOALS.map((g, i) => (
          <SectionReveal key={g.id} delay={i * 60}>
            <GoalCard
              title={copy.goalCopy[g.id].title}
              tagline={copy.goalCopy[g.id].tagline}
              icon={g.icon}
              selected={goal === g.id}
              onSelect={() => onSelect(g.id)}
            />
          </SectionReveal>
        ))}
      </div>
    </div>
  );
}
